/** Branding CSS, import-free: the mockup demo builder (build-demo.mjs) transpiles this on its own. */

/** Accepts #RRGGBB and the #RGB shorthand Bootstrap emits for its own tokens (e.g. --bs-body-bg: #fff). */
function parseRgb(hex: string): number[] {
    const full = hex.replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, '$1$1$2$2$3$3');
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(full);
    return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : [0, 0, 0];
}

export function hexToRgb(hex: string): string {
    return parseRgb(hex).join(', ');
}

/** Relative luminance per WCAG 2.x from an [r,g,b] triple (0-255). */
function relativeLuminance(rgb: number[]): number {
    const [r, g, b] = rgb.map((c) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastRatio(a: number[], b: number[]): number {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** WCAG contrast ratio (1-21) between two #RRGGBB colours. */
export function contrastRatioOn(a: string, b: string): number {
    return contrastRatio(parseRgb(a), parseRgb(b));
}

function blend(base: number, background: number, fade: number): number {
    return Math.round(base * (1 - fade) + background * fade);
}

const MUTED_BLEND = 0.35;
const MIN_CONTRAST = 4.5;

/** Black or white ink for text on the given background; muted blends toward the background for a softer grey, backed off to hold 4.5:1. */
export function contrastInk(bg: string, muted = false): string {
    const rgb = parseRgb(bg);
    const base = relativeLuminance(rgb) > 0.179 ? 0 : 255;

    let amount = muted ? MUTED_BLEND : 0;
    while (amount > 0 && contrastRatio(rgb.map((c) => blend(base, c, amount)), rgb) < MIN_CONTRAST) {
        amount = Math.max(amount - 0.05, 0);
    }

    return '#' + rgb.map((c) => blend(base, c, amount).toString(16).padStart(2, '0')).join('');
}

export function buildBrandingOverrides(bsName: string, kendoName: string, hex: string): string {
    const rgb = hexToRgb(hex);
    const ink = contrastInk(hex);
    return [
        `:root {`,
        `  --bs-${bsName}-rgb: ${rgb};`,
        `  --bs-${bsName}-text-emphasis: color-mix(in srgb, ${hex} 40%, black);`,
        `  --bs-${bsName}-bg-subtle: color-mix(in srgb, ${hex} 10%, white);`,
        `  --bs-${bsName}-border-subtle: color-mix(in srgb, ${hex} 30%, white);`,
        `  --kendo-color-${kendoName}: ${hex};`,
        `  --kendo-color-${kendoName}-hover: color-mix(in srgb, ${hex} 85%, black);`,
        `  --kendo-color-${kendoName}-active: color-mix(in srgb, ${hex} 80%, black);`,
        `  --kendo-color-${kendoName}-emphasis: color-mix(in srgb, ${hex} 40%, transparent);`,
        `  --kendo-color-${kendoName}-subtle: color-mix(in srgb, ${hex} 15%, white);`,
        `  --kendo-color-${kendoName}-subtle-hover: color-mix(in srgb, ${hex} 25%, white);`,
        `  --kendo-color-${kendoName}-subtle-active: color-mix(in srgb, ${hex} 35%, white);`,
        `}`,
        `.btn-${bsName} { --bs-btn-bg: ${hex}; --bs-btn-border-color: ${hex}; --bs-btn-color: ${ink}; --bs-btn-hover-bg: ${hex}; --bs-btn-hover-border-color: ${hex}; --bs-btn-hover-color: ${ink}; --bs-btn-active-bg: ${hex}; --bs-btn-active-border-color: ${hex}; --bs-btn-active-color: ${ink}; --bs-btn-disabled-bg: ${hex}; --bs-btn-disabled-border-color: ${hex}; --bs-btn-disabled-color: ${ink}; color: ${ink}; }`,
        `.btn-${bsName}:hover, .btn-${bsName}:active, .btn-${bsName}:first-child:active { color: ${ink}; }`,
        `.btn-outline-${bsName} { --bs-btn-color: ${hex}; --bs-btn-border-color: ${hex}; --bs-btn-hover-bg: ${hex}; --bs-btn-hover-color: ${ink}; --bs-btn-hover-border-color: ${hex}; --bs-btn-active-bg: ${hex}; --bs-btn-active-color: ${ink}; --bs-btn-active-border-color: ${hex}; --bs-btn-disabled-color: ${hex}; --bs-btn-disabled-border-color: ${hex}; }`,
    ].join('\n');
}

/**
 * Publishes opt-in brand tokens; the .bg-brand* selectors live in the app stylesheet with Bootstrap fallbacks.
 *
 * @param bg - The brand background hex, or null when the tenant sets no brand background
 * @param accent - The brand accent hex, or null when the tenant sets no brand accent
 */
export function buildBrandTokens(bg: string | null, accent: string | null): string {
    const tokens: string[] = [];
    if (bg) {
        tokens.push(`  --brand-bg: ${bg};`);
        const ink = contrastInk(bg);
        tokens.push(`  --brand-bg-ink: ${ink};`);
        tokens.push(`  --brand-bg-ink-muted: ${contrastInk(bg, true)};`);
        tokens.push(`  --brand-bg-close-filter: ${ink === '#ffffff' ? 'var(--bs-btn-close-white-filter)' : 'none'};`);
        tokens.push(`  --brand-bg-subtle: color-mix(in srgb, ${bg} 15%, var(--bs-body-bg));`);
        tokens.push(`  --brand-bg-subtle-ink: var(--bs-body-color);`);
    }
    if (accent) {
        tokens.push(`  --brand-accent: ${accent};`);
        tokens.push(`  --brand-accent-ink: ${contrastInk(accent)};`);
    }
    return [':root {', ...tokens, '}'].join('\n');
}

export function buildPrimaryExtras(hex: string): string {
    const rgb = hexToRgb(hex);
    return [
        `:root { --bs-link-color: ${hex}; --bs-link-hover-color: ${hex}; }`,
        `a { --bs-link-color-rgb: ${rgb}; }`,
        `.link-primary { color: ${hex} !important; }`,
        `.nav-link { --bs-nav-link-color: ${hex}; --bs-nav-link-hover-color: ${hex}; }`,
        `.nav-pills .nav-link.active { background-color: ${hex}; }`,
        `.form-check-input:checked { background-color: ${hex}; border-color: ${hex}; }`,
        `.form-control:focus, .form-select:focus { border-color: ${hex}; box-shadow: 0 0 0 0.25rem rgba(${rgb}, 0.25); }`,
        `.dropdown-item.active, .dropdown-item:active { background-color: ${hex}; }`,
        `.page-item.active .page-link { background-color: ${hex}; border-color: ${hex}; }`,
        `.progress-bar { --bs-progress-bar-bg: ${hex}; }`,
        `.list-group-item.active { --bs-list-group-active-bg: ${hex}; --bs-list-group-active-border-color: ${hex}; }`,
    ].join('\n');
}
